import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { compact, filter, find, sortBy } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CAREER_PATH_LIST_COLUMNS } from '../../../school/lists/list-display/list-display-path/list-display-career-path/career-path.constant';
import { COLLEGE_PATH_LIST_COLUMNS } from '../../../school/lists/list-display/list-display-path/list-display-college-path/college-path.constant';
import { STUDENT_ACTIVITIES_LIST_COLUMNS } from '../../../school/lists/list-display/list-display-student-activities/list-display-student-activities.component';
import { ModalsService } from '../../../shared/modals/modals.service';
import { PATH_TYPE } from '../../../shared/services/api-service/api-service';
import { ImUser } from '../../../shared/services/im-models/im-user';
import { MadLibGroupings } from '../../../shared/services/mad-lib-services/mad-lib-groupings';
import { IListViewData } from '../../../shared/typings/interfaces/list-view.interface';
import { ICareerPath, ICollegePath } from '../../../shared/typings/interfaces/paths.interface';
import { ISchool } from '../../../shared/typings/interfaces/school.interface';
import { IStudent } from '../../../shared/typings/interfaces/student.interface';
import { IStudentPath } from '../../../shared/typings/interfaces/studentPaths.interface';
import { IUser } from '../../../shared/typings/interfaces/user.interface';
import { CreateCareerPath, CreateStudentActivity } from '../../../store';
import { IStudentPathCreatePayload } from '../../../store/actions/student-paths-actions';
import { StudentPanelService } from '../student-panel.service';
import { LIST_TYPE } from './../../../shared/constants/list-view/list-view.constant';
import { MilestonesKeyMap } from './../../../shared/constants/milestones.constant';
import { ActivitiesGrouping } from './../../../shared/services/mad-lib-services/grouping-functions/postsec-groupings/activities-groupings';
import { IMilestone } from './../../../shared/typings/interfaces/milestones.interface';
import { IStudentActivity } from './../../../shared/typings/interfaces/student-activity.interface';
import { CreateStudentPath } from './../../../store/actions/student-paths-actions';
import { getToggleEntities } from './../../../store/selectors/toggles-selectors';
import { POST_SEC_LIST_COLUMNS } from './post-sec.constant';
import PostsecConfig from './student-postsec.config';

export interface ISummaryDataItem {
  name: string;
  value: number | string;
  tooltip?: string;
  tooltipClass?: string;
  display: boolean;
}
@Component({
  selector: 'student-postsec',
  template: require('./student-postsec.component.html'),
  styles: [require('./student-postsec.scss')],
})
export class StudentPostsecPanel implements OnInit, OnDestroy {
  @Input() student: IStudent;
  @Input() school: ISchool;
  @Input() studentPaths$: Observable<IStudentPath[]>;
  @Input() collegePaths$: Observable<ICollegePath[]>;
  @Input() careerPaths$: Observable<ICareerPath[]>;
  @Input() milestones$: Observable<IMilestone[]>;
  @Input() studentActivities$: Observable<IStudentActivity[]>;
  @Input() currentUser: IUser;
  @Output() saveStudent: EventEmitter<any> = new EventEmitter();
  @Input() collapsed: boolean;

  listOptions: string[];
  panelTitle: string;
  studentId: string;
  selectedListId: number;
  formattedMilestones: IListViewData;
  formattedCollegePaths$: Observable<IListViewData>;
  formattedCareerPaths$: Observable<IListViewData>;
  formattedStudentActivities$: Observable<IListViewData>;
  summaryData: ISummaryDataItem[];
  allMilestones: IMilestone[];
  destroy$: Subject<boolean> = new Subject<boolean>();
  studentPaths: IStudentPath[];
  studentActivities: IStudentActivity[];
  isViewOnlyUser: boolean;
  isEditingUser: boolean;
  showPostsecExp: boolean;
  currentSchoolYear: string;

  get selectedList() {
    return this.listOptions[this.selectedListId];
  }

  constructor(
    private studentPanel: StudentPanelService,
    private madLibGroupings: MadLibGroupings,
    private activitiesGrouping: ActivitiesGrouping,
    private modalsService: ModalsService,
    private store: Store<any>,
    private imUser: ImUser,
  ) {}

  ngOnInit() {
    const term = find(this.school.termInfo, term => term.currentTermInd);
    this.currentSchoolYear = term.schoolYear;
    this.panelTitle = PostsecConfig.title;
    this.collapsed = this.studentPanel.getInitialPanelState('student-postsec-panel');
    this.studentId = this.student._id;
    this.selectedListId = 0; // = this.listOptions[0]; // College
    this.listOptions = this.getSegmentedControlOptions();
    this.setSummaryDataItems();
    this.formattedCollegePaths$ = this.getFormattedStudentPaths(this.studentPaths$, LIST_TYPE.COLLEGE_PATH);
    this.formattedCareerPaths$ = this.getFormattedStudentPaths(this.studentPaths$, LIST_TYPE.CAREER_PATH);
    this.formattedStudentActivities$ = this.getFormattedStudentActivities(this.studentActivities$);
    this.studentPaths$.pipe(takeUntil(this.destroy$)).subscribe(studentPaths => {
      this.studentPaths = filter(studentPaths, studentPath => {
        const { status } = studentPath;
        const isDeleted = status === 'DELETED';
        const isClosed = status === 'CLOSED';
        return studentPath.studentId === this.student._id && !isDeleted && !isClosed;
      });
      // TODO: calling `getFormattedMilestones` here because if the milestone patch is returned before the
      // student paths patch, then the milestones table will not reflect the student paths patch.
      // More info: https://www.pivotaltracker.com/story/show/160634796
      this.getFormattedMilestones();
    });
    this.milestones$.pipe(takeUntil(this.destroy$)).subscribe(allMilestones => {
      this.allMilestones = allMilestones;
      this.getFormattedMilestones();
    });
    this.studentActivities$.pipe(takeUntil(this.destroy$)).subscribe(studentActivities => {
      this.studentActivities = studentActivities;
    });
    this.isViewOnlyUser = this.imUser.isViewType(this.currentUser);
    this.isEditingUser = this.imUser.isEditingUser(this.currentUser);
    this.store.select(getToggleEntities).subscribe(toggleEntities => {
      this.showPostsecExp = toggleEntities.TOGGLE_POSTSEC_EXPERIENCES;
      this.listOptions = this.getSegmentedControlOptions();
    });
    this.store
      .select(getToggleEntities)
      .pipe(takeUntil(this.destroy$))
      .subscribe(toggleEntities => {
        this.showPostsecExp = toggleEntities.TOGGLE_POSTSEC_EXPERIENCES;
      });
  }

  getFormattedStudentPaths(studentPaths$, listType: LIST_TYPE): Observable<any> {
    if (listType === LIST_TYPE.COLLEGE_PATH) {
      return this.madLibGroupings.getCollegePathGrouping(
        studentPaths$,
        COLLEGE_PATH_LIST_COLUMNS,
        listType,
        this.studentId,
      );
    } else if (listType === LIST_TYPE.CAREER_PATH) {
      return this.madLibGroupings.getCareerPathGrouping(
        studentPaths$,
        CAREER_PATH_LIST_COLUMNS,
        listType,
        this.studentId,
      );
    }
  }

  getFormattedStudentActivities(studentActivities$): Observable<any> {
    return this.activitiesGrouping.getStudentActivitiesGrouping(
      studentActivities$,
      STUDENT_ACTIVITIES_LIST_COLUMNS,
      LIST_TYPE.STUDENT_ACTIVITIES,
      this.studentId,
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  getFormattedMilestones() {
    const filteredMilestones = filter(this.allMilestones, (milestone: IMilestone) => {
      const milestoneProps = MilestonesKeyMap[milestone.key];
      // props used to filter out milestones with inaccurate 'isCollege' or 'isCareer' fields (JE)
      if (milestoneProps) {
        const { collegeOrder, careerOrder } = milestoneProps;
        const isStudentMilestone = milestone.studentId === this.studentId;
        if (this.selectedListId === 0) {
          return collegeOrder && isStudentMilestone;
        } else {
          return careerOrder && isStudentMilestone;
        }
      }
    });

    const groupingObj = {
      studentMilestones: filter(filteredMilestones, ['studentId', this.studentId]),
      listType: LIST_TYPE.MILESTONE,
      columns: POST_SEC_LIST_COLUMNS,
      studentPaths: this.studentPaths,
    };
    const milestonesData = this.madLibGroupings.getMilestoneGroupings(groupingObj);
    if (this.selectedListId === 0) {
      milestonesData.sections[0].data = sortBy(milestonesData.sections[0].data, (d: any) => {
        return d.milestone.collegeOrder;
      });
    } else {
      milestonesData.sections[0].data = sortBy(milestonesData.sections[0].data, (d: any) => {
        return d.milestone.careerOrder;
      });
      milestonesData.sections[0].name = 'Career milestones';
    }
    this.formattedMilestones = milestonesData;
  }

  togglePanelBody(): void {
    this.collapsed = this.studentPanel.togglePanelBody(this.collapsed, this.panelTitle);
  }

  addCollege() {
    this.modalsService
      .openStudentPathsModal({
        mode: 'ADD',
        schoolId: this.student.schoolId,
        isProfileMode: true,
        studentIds: [this.studentId],
        studentPaths: this.studentPaths,
      })
      .afterClosed()
      .subscribe(selectedCollege => {
        if (selectedCollege) {
          const payload: IStudentPathCreatePayload = {
            schoolId: this.school._id,
            studentId: this.studentId,
            pathId: selectedCollege.pathId,
          };
          this.store.dispatch(new CreateStudentPath(payload));
        }
      });
  }

  addCareer() {
    this.modalsService
      .openAddCareerModal({
        careerPath: this.careerPaths$,
        studentId: this.studentId,
      })
      .afterClosed()
      .subscribe(careerOption => {
        if (careerOption) {
          if (!careerOption.isNewCareer) {
            // an existing path has been selected. add it to studentPath
            const payload: IStudentPathCreatePayload = {
              schoolId: this.school._id,
              studentId: this.studentId,
              pathId: careerOption.selectedOption._id,
            };
            this.store.dispatch(new CreateStudentPath(payload));
          } else {
            const payload = {
              schoolId: this.school._id,
              pathType: PATH_TYPE.CAREER,
              studentId: this.studentId,
              payload: {
                name: careerOption.career.trim(),
                careerType: careerOption.careerType,
              },
            };
            this.store.dispatch(new CreateCareerPath(payload));
          }
        }
      });
  }

  addStudentActivity(activity) {
    this.modalsService
      .openStudentActivitiesModal({
        mode: 'CREATE',
        schoolId: this.student.schoolId,
        currentSchoolYear: this.currentSchoolYear,
        studentIds: [this.studentId],
        isProfileMode: true,
        activity,
        currentUser: this.currentUser,
      })
      .afterClosed()
      .takeUntil(this.destroy$)
      .subscribe(payload => {
        if (payload && payload.returnTo) {
          const { returnTo } = payload;
          this.modalsService
            .openActivitiesModal({
              mode: 'CREATE',
              schoolId: this.student.schoolId,
              isProfileMode: true,
            })
            .afterClosed()
            .takeUntil(this.destroy$)
            .subscribe(activityPayload => {
              if (returnTo === 'StudentActivitiesModal') {
                this.addStudentActivity(activityPayload);
              }
            });
        } else if (payload && payload.confirmModalData) {
          const { confirmModalData, studentActivityPayload } = payload;
          this.modalsService
            .openConfirmModal(confirmModalData)
            .afterClosed()
            .takeUntil(this.destroy$)
            .subscribe((confirmed: false | true) => {
              if (confirmed) {
                // dispatch action to assign student to activity
                this.store.dispatch(new CreateStudentActivity({ studentActivityParams: studentActivityPayload }));
              } else {
                this.addStudentActivity(activity);
              }
            });
        }
      });
  }

  setSummaryDataItems(): void {
    const gpaSummary = this._buildGPA();
    const cunyCollegeReadinessSummary = this._buildCunyCollegeReadiness();
    const maxPSATSummary = this._buildMaxPSAT();
    const maxSATSummary = this._buildMaxSAT();
    const { isCharter } = this.student.charterDetails;
    const maxACTSummary = isCharter ? this._buildMaxACT() : null;
    // remove until 'advanced' defined by data team (JE)
    // const advancedCoursesSummary = this._buildAdvancedCourses();
    const summaryItems = [
      gpaSummary,
      cunyCollegeReadinessSummary,
      maxPSATSummary,
      maxSATSummary,
      maxACTSummary,
      // advancedCoursesSummary
    ];

    this.summaryData = compact(summaryItems);
  }

  _buildGPA(): ISummaryDataItem {
    const { GPA } = this.student.creditDetails;
    return {
      name: 'GPA',
      value: GPA || '—',
      display: true,
    };
  }

  _buildCunyCollegeReadiness(): ISummaryDataItem {
    const { isELACR, isMathCR, SAT } = this.student.postsecondary;
    const {
      byCategory5: { ela, math },
    } = this.student.regentsDetails;
    let cunyVal;
    if (isELACR && isMathCR) cunyVal = '2/2';
    else if ((isELACR && !isMathCR) || (!isELACR && isMathCR)) cunyVal = '1/2';
    else cunyVal = '0/2';

    const cunyElaReady = isELACR ? 'College ready' : 'Not ready';
    const cunyElaTooltip = `<div class="tooltip-score">ELA: ${cunyElaReady}</div>`;

    const maxELARegents = ela.maxScore.string;
    const cunyMaxELATooltip =
      `<div class="tooltip-text">Max ELA Regents: ` +
      `<span class="tooltip-score">${maxELARegents ? maxELARegents : '—'}</span></div>`;
    const cunySATReadWriteTooltip =
      `<div class="tooltip-text">Max SAT reading & writing: ` +
      `<span class="tooltip-score">${SAT.readingMax ? SAT.readingMax : '—'}</span></div><br>`;

    const cunyMathReady = isMathCR ? 'College ready' : 'Not ready';
    const cunyMathTooltip = `<div class="tooltip-score">Math: ${cunyMathReady}</div>`;

    const maxMathRegents = math.maxScore.string;
    const cunyMaxMathTooltip =
      `<div class="tooltip-text">Max math Regents: ` +
      `<span class="tooltip-score">${maxMathRegents ? maxMathRegents : '—'}</span></div>`;
    const cunySATMathTooltip =
      `<div class="tooltip-text">Max SAT reading & writing: ` +
      `<span class="tooltip-score">${SAT.mathMax ? SAT.mathMax : '—'}</span></div>`;

    const fullTooltip =
      cunyElaTooltip +
      cunyMaxELATooltip +
      cunySATReadWriteTooltip +
      cunyMathTooltip +
      cunyMaxMathTooltip +
      cunySATMathTooltip;

    return {
      name: 'CUNY COLLEGE READINESS',
      value: cunyVal,
      tooltip: fullTooltip,
      tooltipClass: 'tooltip-wide',
      display: true,
    };
  }

  _buildMaxPSAT(): ISummaryDataItem {
    const {
      PSAT: { totalMax, readingMax, mathMax, attempts },
    } = this.student.postsecondary;

    const tooltip =
      `<span class="tooltip-text">Max reading & writing: ` +
      `<span class="tooltip-score">${readingMax ? readingMax : '—'}</span></span><br>` +
      `<span class="tooltip-text">Max math: ` +
      `<span class="tooltip-score">${mathMax ? mathMax : '—'}</span></span><br>` +
      `<span class="tooltip-text">Attempts: ` +
      `<span class="tooltip-score">${attempts ? attempts : '—'}</span></span>`;

    return {
      name: 'MAX PSAT',
      value: totalMax || '—',
      tooltip,
      tooltipClass: 'tooltip-med',
      display: true,
    };
  }

  _buildMaxSAT(): ISummaryDataItem {
    const {
      SAT: { readingMax, mathMax, attempts, superScore },
    } = this.student.postsecondary;

    const tooltip =
      `<span class="tooltip-text">Max reading & writing: ` +
      `<span class="tooltip-score">${readingMax ? readingMax : '—'}</span></span><br>` +
      `<span class="tooltip-text">Max math: ` +
      `<span class="tooltip-score">${mathMax ? mathMax : '—'}</span></span><br>` +
      `<span class="tooltip-text">Attempts: ` +
      `<span class="tooltip-score">${attempts ? attempts : '—'}</span></span>`;

    return {
      name: 'SAT Superscore',
      value: superScore || '—',
      tooltip,
      tooltipClass: 'tooltip-med',
      display: true,
    };
  }

  _buildMaxACT(): ISummaryDataItem {
    // TODO: update this summary item once the schema updates for ACT are in (JE)
    const {
      ACT: { readingMax, mathMax, compositeMax },
    } = this.student.postsecondary;

    const tooltip =
      `<span class="tooltip-text">English: ` +
      `<span class="tooltip-score">${readingMax ? readingMax : '—'}</span></span><br>` +
      `<span class="tooltip-text">Math: ` +
      `<span class="tooltip-score">${mathMax ? mathMax : '—'}</span></span>`;
    // TODO: add 'Attempts' once it is on the schema (JE)

    return {
      name: 'MAX ACT',
      value: compositeMax || '—', // total missing in schema (JE)
      tooltip,
      tooltipClass: 'tooltip-med',
      display: true,
    };
  }

  onListToggle(listId: number): void {
    this.selectedListId = listId;
    this.getFormattedMilestones();
  }

  // TODO: remove this method when Postsec Exp toggel is gone (JE)
  /* istanbul ignore next */
  getSegmentedControlOptions(): string[] {
    if (this.showPostsecExp) return ['College', 'Career', 'Activities'];
    else return ['College', 'Career'];
  }
}
